import { PaymentContext } from '../contexts/payment'
import { useCallback, useContext, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Payment, SafeSecureBanner } from '../components/payment'
import { Navigate, useNavigate } from 'react-router-dom'
import { PaymentService } from '../services/payments'
import Container from '../components/container'
import PaymentDetails from '../components/payment-details'

export default function InitiatePayment (): JSX.Element {
  const { payment } = useContext(PaymentContext)
  const { t } = useTranslation()
  const navigate = useNavigate()

  const redirectUri = useMemo(() => `/payments/${payment.id}/completed`, [payment.id])

  const getIntent = useCallback(async (paymentMethodId: string) => {
    const paymentService = new PaymentService()
    return await paymentService.createPayment(payment.id, paymentMethodId)
  }, [payment.id])

  const onComplete = useCallback(() => {
    navigate(`${redirectUri}?redirect_status=succeeded`)
  }, [navigate, redirectUri])

  if (payment.status === 'Processing') {
    return (<Navigate to={`${redirectUri}?redirect_status=processing`} />)
  } else if (payment.status !== 'Open') {
    return (<Navigate to={`${redirectUri}?redirect_status=succeeded`} />)
  }

  return (
    <Container>
      <div className='grid grid-cols-1 lg:grid-cols-6 gap-x-10 gap-y-0 lg:gap-y-4'>
        <div className='lg:col-span-3'>
          <h2
            className='text-brand-primary text-lg font-bold my-8'
          >{t('payment:payment_title', 'Fulfill your outstanding payment')}
          </h2>
          <p
            className='my-3'
          >{t('payment:payment_description', 'Please proceed with the payment for your booking.')}
          </p>
          <PaymentDetails payment={payment} />
        </div>
        {payment.amount > 0 && (
          <>
            <div className='lg:col-span-3 mb-8 flex flex-col justify-end'>
              <div className='flex flex-col lg:items-end'>
                {(payment.market !== 'north_america' || payment.country_code !== 'CA') && (
                  <SafeSecureBanner stripeLogo />)}
              </div>
            </div>
            <div className='lg:col-span-6'>
              <Payment
                redirectUri={redirectUri} market={payment.market} amount={Math.round(payment.amount * 100)}
                getIntent={getIntent} billingDetails={{ name: payment.full_name, email: payment.email }}
                countryCode={payment.country_code} bookingNumber={payment.booking_number ?? ''}
                exchangeRates={payment.exchange_rates} onComplete={onComplete}
              />
            </div>
          </>
        )}
      </div>
    </Container>
  )
}
